import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { siteConfig } from "../config/site";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { AvailabilityButton } from "./BookingButton";
import { track } from "../lib/tracking";

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const primary = siteConfig.contacts[0];
  const isHome = pathname === "/";
  const light = isHome && !scrolled && !open;

  const nav = [
    { to: "/zimmer", label: t.nav.rooms },
    { to: "/ausstattung", label: t.nav.amenities },
    { to: "/lage", label: t.nav.location },
    { to: "/galerie", label: t.nav.gallery },
    { to: "/faq", label: t.nav.faq },
    { to: "/kontakt", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${scrolled || open ? "bg-cream/85 backdrop-blur-xl shadow-[0_1px_0_rgba(28,26,21,0.06)]" : "bg-transparent"}`} data-testid="site-header">
      <div className="container-x flex h-[72px] items-center justify-between gap-4 md:h-20">
        <Link to="/" className="group flex items-baseline gap-1.5" data-testid="logo-link" aria-label={siteConfig.name}>
          <span className={`font-serif text-2xl tracking-tight transition-colors duration-300 ${light ? "text-cream" : "text-charcoal"}`}>Kola</span>
          <span className={`font-serif text-2xl italic transition-colors duration-300 ${light ? "text-cream/80" : "text-charcoal-500"}`}>Pension</span>
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Hauptnavigation" data-testid="desktop-nav">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} className={({ isActive }) => `relative whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${light ? (isActive ? "text-cream" : "text-cream/75 hover:text-cream") : (isActive ? "text-charcoal" : "text-charcoal-500 hover:text-charcoal")}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <LanguageSwitcher className="hidden xl:inline-flex" />
          <a href={`tel:${primary.phoneHref}`} className={`btn-ghost whitespace-nowrap !px-4 ${light ? "!text-cream hover:!bg-white/15" : ""}`} data-testid="header-phone-button" onClick={() => track("phone_click")}>
            <Phone className="h-4 w-4" aria-hidden /> <span>{primary.phone}</span>
          </a>
          <AvailabilityButton testId="header-availability-button" className={`whitespace-nowrap ${light ? "!bg-cream !text-charcoal hover:!bg-white" : ""}`} />
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a href={`tel:${primary.phoneHref}`} className={`btn-ghost !p-3 ${light ? "!text-cream hover:!bg-white/15" : ""}`} aria-label={t.cta.callNow} data-testid="mobile-phone-button" onClick={() => track("phone_click")}>
            <Phone className="h-4 w-4" aria-hidden />
          </a>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t.nav.close : t.nav.menu} className={`btn !p-3 ${light ? "bg-cream text-charcoal hover:bg-white" : "bg-charcoal text-cream hover:bg-charcoal-700"}`} data-testid="mobile-menu-toggle">
            {open ? <X className="h-4 w-4" aria-hidden /> : <Menu className="h-4 w-4" aria-hidden />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div id="mobile-menu" className="lg:hidden border-t border-sand-200 bg-cream/95 backdrop-blur-xl" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: "easeOut" }} data-testid="mobile-menu">
            <nav className="container-x flex flex-col gap-1 py-6" aria-label="Mobile Navigation">
              <NavLink to="/" className="rounded-2xl px-4 py-3 text-lg font-medium hover:bg-sand-50" data-testid="mobile-nav-home">{t.nav.home}</NavLink>
              {nav.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
                  <NavLink to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`} className={({ isActive }) => `block rounded-2xl px-4 py-3 text-lg font-medium hover:bg-sand-50 ${isActive ? "text-clay" : ""}`}>{n.label}</NavLink>
                </motion.div>
              ))}
              <NavLink to="/ueber-uns" className="rounded-2xl px-4 py-3 text-lg font-medium hover:bg-sand-50" data-testid="mobile-nav-ueber-uns">{t.nav.about}</NavLink>
              <div className="mt-4 flex flex-col gap-3 border-t border-sand-200 pt-6">
                <AvailabilityButton testId="mobile-availability-button" className="w-full" />
                <a href={`tel:${primary.phoneHref}`} className="btn-outline w-full" data-testid="mobile-menu-phone" onClick={() => track("phone_click")}><Phone className="h-4 w-4" aria-hidden /> {primary.phone}</a>
                <div className="flex justify-center pt-2"><LanguageSwitcher /></div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
